import { Table, Transfer, Button } from 'antd'
import type { ColumnType } from 'antd/lib/table'
import type { TableProps } from 'antd/lib/table'
import type { TransferProps, TransferItem } from 'antd/lib/transfer'
import { createElement, useMemo, useState } from 'react'
import { ComponentController } from './component_controller'
import { dispatcher } from '../pub_controller'

interface TableTransferItem extends TransferItem {
  [key: string]: any
}

interface CustomColumnType<RecordType> extends ColumnType<RecordType> {
  sortType?: 'string' | 'number'
  editable?: boolean
  editModalUrl?: string
  modalParams?: string
}

interface TableTransferProps
  extends Omit<TransferProps<TableTransferItem>, 'children'> {
  columns: CustomColumnType<TableTransferItem>[]
  pageSize?: number
}

const buildFilterOption =
  (columns: CustomColumnType<TableTransferItem>[]) =>
  (input: string, item: TableTransferItem) => {
    const searchInput = input.toLowerCase()
    return columns.some((column) => {
      const value = item[column.dataIndex as string]
      return typeof value === 'string'
        ? value.toLowerCase().includes(searchInput)
        : false
    })
  }

const DEFAULT_PAGE_SIZE = 5

const addDefaultSorters = (columns: CustomColumnType<TableTransferItem>[]) => {
  return columns.map((col) => {
    const dataIndex = col.dataIndex as string
    if (!col.sorter) {
      if (col.sortType === 'number') {
        return {
          ...col,
          sorter: (a: TableTransferItem, b: TableTransferItem) => {
            const aVal = a[dataIndex] || 0
            const bVal = b[dataIndex] || 0
            return aVal - bVal
          },
        }
      } else {
        return {
          ...col,
          sorter: (a: TableTransferItem, b: TableTransferItem) => {
            const aVal = (a[dataIndex] || '').toString()
            const bVal = (b[dataIndex] || '').toString()
            return aVal.localeCompare(bVal)
          },
        }
      }
    }
    return col
  })
}

const renderEditableCell = (
  value: any,
  record: TableTransferItem,
  editModalUrl: string,
  modalParams?: string,
) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const url = new URL(
      editModalUrl.replace('UUID', record.key.toString()),
      window.location.origin,
    )
    const params = modalParams?.split(',')
    params.forEach((param) => {
      url.searchParams.set(param, record[param] || '')
    })

    const event = new CustomEvent('variance_modal:turbo-modal-get', {
      bubbles: true,
      detail: { value: url.toString() },
    })
    window.dispatchEvent(event)
  }

  return createElement('div', { className: 'transfer-editable-cell' }, [
    createElement('span', { key: 'value' }, value),
    createElement(
      'a',
      {
        key: 'edit-link',
        href: '#',
        onClick: handleClick,
      },
      [
        createElement('div', {
          key: 'edit-icon',
          'data-controller': 'ant-icon',
          'data-ant-icon-props': JSON.stringify({ icon: 'edit' }),
        }),
      ],
    ),
  ])
}

const buildTransferColumnTable = (
  sortableColumns: CustomColumnType<TableTransferItem>[],
  pageSize: number,
  sortedInfo: {
    columnKey: string | null
    order: 'ascend' | 'descend' | null
  },
  handleTableChange: TableProps<TableTransferItem>['onChange'],
) => {
  const TransferColumnTable = ({
    filteredItems,
    onItemSelect,
    onItemSelectAll,
    selectedKeys: listSelectedKeys,
  }: {
    filteredItems: TableTransferItem[]
    onItemSelect: (key: string, selected: boolean) => void
    onItemSelectAll: (
      keys: string[],
      direction: 'left' | 'right' | 'replace',
    ) => void
    selectedKeys: string[]
  }) => {
    const onRow = ({ key, disabled: itemDisabled }: TableTransferItem) => ({
      onClick: () => {
        if (itemDisabled || !key) {
          return
        }
        onItemSelect(key.toString(), !listSelectedKeys.includes(key.toString()))
      },
    })
    const rowSelection = {
      getCheckboxProps: (item: TableTransferItem) =>
        ({
          'aria-label': item.identifier,
        }) as any,
      onChange(selectedRowKeys: string[]) {
        onItemSelectAll(selectedRowKeys, 'replace')
      },
      selectedRowKeys: listSelectedKeys,
      selections: [
        Table.SELECTION_ALL,
        Table.SELECTION_INVERT,
        Table.SELECTION_NONE,
      ],
    }
    return createElement(Table, {
      columns: sortableColumns.map((col) => ({
        ...col,
        sortOrder:
          sortedInfo.columnKey === col.dataIndex ? sortedInfo.order : null,
        render: col.editable
          ? (value: any, record: TableTransferItem) =>
              renderEditableCell(
                value,
                record,
                col.editModalUrl,
                col.modalParams,
              )
          : col.render,
      })),
      dataSource: filteredItems,
      onRow,
      onChange: handleTableChange,
      pagination: { pageSize, size: 'small', position: ['bottomCenter'] },
      rowSelection,
      size: 'small',
    })
  }
  return TransferColumnTable
}

const VerticalTransfer = ({
  columns,
  pageSize = DEFAULT_PAGE_SIZE,
  ...options
}: TableTransferProps) => {
  const [sortedInfo, setSortedInfo] = useState<{
    columnKey: string | null
    order: 'ascend' | 'descend' | null
  }>({
    columnKey: null,
    order: null,
  })

  const handleTableChange: TableProps<TableTransferItem>['onChange'] = (
    pagination,
    filters,
    sorter,
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    setSortedInfo({
      columnKey: singleSorter?.field?.toString() || null,
      order: singleSorter?.order || null,
    })
  }

  const filterOption = useMemo(() => buildFilterOption(columns), [columns])
  const sortableColumns = useMemo(() => addDefaultSorters(columns), [columns])
  const TransferColumnTable = useMemo(
    () =>
      buildTransferColumnTable(
        sortableColumns,
        pageSize,
        sortedInfo,
        handleTableChange,
      ),
    [sortableColumns, pageSize, sortedInfo],
  )

  const transferProps: TransferProps<TableTransferItem> = {
    className: 'ant-transfer-vertical',
    filterOption,
    showSearch: true,
    ...options,
  }
  return createElement(Transfer, transferProps, TransferColumnTable as any)
}

export class VerticalTransferController extends ComponentController<TableTransferProps> {
  static targets = ['component', 'input']

  declare componentTarget: HTMLElement
  declare inputTarget: HTMLInputElement

  updatedItems = new Map()

  handleUpdate(event: CustomEvent) {
    const { id, updates } = event.detail
    const dataSource = this.propsFromData.dataSource || []

    const currentTargetKeys = this.inputTarget.value
      ? this.inputTarget.value.split(',')
      : []

    this.updatedItems.set(id, updates)
    const updatedDataSource = dataSource.map((item) => {
      const storedUpdates = this.updatedItems.get(item.key)
      if (storedUpdates) {
        return { ...item, ...storedUpdates }
      }
      return item
    })

    if (!currentTargetKeys.includes(id)) {
      currentTargetKeys.push(id)
    }

    this.updateProps({
      ...this.propsFromData,
      dataSource: updatedDataSource,
      targetKeys: currentTargetKeys,
    })
  }

  onChange = (targetKeys) => {
    dispatcher(this, 'change', 'vertical-transfer-table', {})
    this.inputTarget.value = targetKeys.join(',')
    this.updateProps({ targetKeys: targetKeys })
  }

  get component() {
    return VerticalTransfer
  }

  get initialProps() {
    return {
      onChange: this.onChange,
      showSelectAll: true,
      ...this.propsFromData,
    }
  }

  get rootElement() {
    return this.componentTarget
  }
}
